// 星盘锁页面（月亮塔罗）：谜诗 + 圆形星盘（4–5 格）+ 本章符文托盘。
// 拖动或点按把符文放进格子；全部放好后「转动星盘」。答错 = 星盘轻轻晃一下，不罚；答错 3 次出现「提示」。
// 答对 = 金色旋转，然后翻开《月亮之书》的一页。状态（格子、答错次数、提示）由 App 存档，这里只管 DOM。
import { runeSvg } from './runeIcons';

export type DialRune = { id: string; name: string; icon: string; found: boolean; card: string };
export type DialModel = {
  title: string;
  /** 「第一章 · 启程」 */
  chapter: string;
  poem: string[];
  /** 托盘：本章全部符文（按牌序） */
  runes: DialRune[];
  /** 当前格子（符文 id = 地点 id；null = 空） */
  slots: (string | null)[];
  /** 提示揭开的前几格的答案（这几格锁定） */
  hinted: string[];
  wrong: number;
  solved: boolean;
  page: { book: string; title: string; text: string };
  /** 最后一章：合上书之后进入终章 */
  last: boolean;
};
export type DialActions = {
  /** 格子变了（存档） */
  change: (slots: (string | null)[]) => void;
  /** 转动星盘：true = 答对（App 记账） */
  check: (slots: string[]) => boolean;
  /** 揭开下一格；返回新的提示答案列表（没有可揭的 = null） */
  hint: () => string[] | null;
  /** 点了还没找到的符文 */
  missing: (r: DialRune) => void;
  /** 合上书 */
  close: () => void;
};

const CN = ['一', '二', '三', '四', '五', '六'];
const SHAKE_MS = 520;
const SPIN_MS = 1700;
export const HINT_AFTER = 3;

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

export class DialView {
  readonly root: HTMLElement;
  private slots: (string | null)[];
  private hinted: string[];
  private wrong: number;
  private solved: boolean;
  private selected = -1;
  private readonly slotEls: HTMLButtonElement[] = [];
  private readonly tileEls = new Map<string, HTMLButtonElement>();
  private readonly wheel: HTMLElement;
  private readonly status: HTMLElement;
  private readonly turn: HTMLButtonElement;
  private readonly hintBtn: HTMLButtonElement;
  private readonly byId: Map<string, DialRune>;
  private drag: { id: string; x: number; y: number; ghost: HTMLElement | null; pointer: number; from: HTMLElement } | null = null;
  private busy = false;

  constructor(private readonly m: DialModel, private readonly a: DialActions) {
    this.slots = [...m.slots];
    this.hinted = [...m.hinted];
    this.wrong = m.wrong;
    this.solved = m.solved;
    this.byId = new Map(m.runes.map((r) => [r.id, r]));
    const root = el('div', 'dial-page');
    this.root = root;

    const poem = el('section', 'dial-poem');
    poem.append(el('p', 'dial-chapter', m.chapter));
    const lines = el('div', 'dial-lines');
    for (const line of m.poem) lines.append(el('p', 'dial-line', line));
    poem.append(lines);

    this.wheel = el('div', 'dial-wheel');
    this.wheel.dataset.slots = String(this.slots.length);
    const disk = el('div', 'dial-disk');
    disk.append(el('span', 'dial-ring dial-ring--outer'), el('span', 'dial-ring dial-ring--inner'));
    const hub = el('span', 'dial-hub');
    hub.append(runeSvg('sleeping-moon', 'dial-hub-icon'));
    disk.append(hub);
    this.wheel.append(disk);
    const n = this.slots.length;
    for (let i = 0; i < n; i++) {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
      // 盘面上的连线：从中心到每一格
      const spoke = el('span', 'dial-spoke');
      spoke.style.transform = `rotate(${(a * 180) / Math.PI}deg)`;
      disk.append(spoke);
      const b = el('button', 'dial-slot');
      b.type = 'button';
      b.dataset.slot = String(i);
      b.style.left = `${50 + Math.cos(a) * 34}%`;
      b.style.top = `${50 + Math.sin(a) * 34}%`;
      b.onclick = () => this.tapSlot(i);
      this.slotEls.push(b);
      this.wheel.append(b);
    }

    this.status = el('p', 'dial-status');
    this.status.setAttribute('role', 'status');

    const tray = el('div', 'dial-tray');
    for (const r of m.runes) {
      const t = el('button', `dial-rune${r.found ? '' : ' is-missing'}`);
      t.type = 'button';
      t.dataset.rune = r.id;
      if (r.found) {
        t.append(runeSvg(r.icon, 'dial-rune-icon'), el('span', 'dial-rune-name', r.name));
        t.setAttribute('aria-label', `符文 ${r.name}`);
        t.addEventListener('pointerdown', (e) => this.dragStart(e, r.id, t));
      } else {
        const lock = el('span', 'dial-rune-lock');
        lock.append(runeSvg(r.icon, 'dial-rune-icon'));
        t.append(lock, el('span', 'dial-rune-name', `${r.card}`), el('span', 'dial-rune-miss', '回到那张牌找找'));
        t.setAttribute('aria-label', `还没找到：回到「${r.card}」找找`);
        t.onclick = () => {
          this.a.missing(r);
          this.setStatus(`回到「${r.card}」的立体书里，找找它的符文`);
          t.classList.remove('is-nudge');
          void t.offsetWidth;
          t.classList.add('is-nudge');
        };
      }
      this.tileEls.set(r.id, t);
      tray.append(t);
    }

    const actions = el('div', 'dial-actions');
    this.hintBtn = el('button', 'ui-btn ui-btn--blue dial-hint');
    this.hintBtn.type = 'button';
    this.hintBtn.id = 'dial-hint';
    this.hintBtn.append(el('span', '', '提示'));
    this.hintBtn.onclick = () => this.useHint();
    this.turn = el('button', 'ui-btn ui-btn--yellow ui-btn--wide dial-turn');
    this.turn.type = 'button';
    this.turn.id = 'dial-turn';
    this.turn.append(el('span', '', '转动星盘'));
    this.turn.onclick = () => this.check();
    actions.append(this.hintBtn, this.turn);

    root.append(poem, this.wheel, this.status, tray, actions);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onCancel);
    this.render();
    if (this.solved) {
      this.wheel.classList.add('is-solved', 'is-done');
      this.showPage(false);
    }
  }

  /** 离开页面时调用：摘掉 window 上的监听 */
  dispose(): void {
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onCancel);
    this.drag?.ghost?.remove();
    this.drag = null;
  }

  private filled(i: number): string | null {
    if (i < this.hinted.length) {
      const id = this.hinted[i];
      return this.byId.get(id)?.found ? id : null;
    }
    return this.slots[i];
  }

  private render(): void {
    const used = new Set<string>();
    this.slotEls.forEach((b, i) => {
      const hint = i < this.hinted.length;
      const id = hint ? this.hinted[i] : this.slots[i];
      const r = id ? this.byId.get(id) : undefined;
      b.replaceChildren(el('span', 'dial-slot-num', CN[i] ?? String(i + 1)));
      b.classList.toggle('is-filled', !!r && (!hint || r.found));
      b.classList.toggle('is-hint', hint);
      b.classList.toggle('is-ghost', hint && !!r && !r.found);
      b.classList.toggle('is-selected', i === this.selected);
      if (r) {
        b.append(runeSvg(r.icon, 'dial-slot-icon'));
        if (!hint || r.found) used.add(r.id);
        b.setAttribute('aria-label', `第${CN[i]}格：${hint && !r.found ? `提示是「${r.name}」，还没找到` : r.name}`);
      } else b.setAttribute('aria-label', `第${CN[i]}格：空`);
    });
    for (const [id, t] of this.tileEls) t.classList.toggle('is-used', used.has(id));
    const all = this.slotEls.every((_, i) => this.filled(i) !== null);
    this.turn.disabled = !all || this.solved || this.busy;
    this.hintBtn.hidden = this.solved || this.wrong < HINT_AFTER || this.hinted.length >= this.slots.length - 1;
    if (this.solved) this.setStatus('星盘转好了');
    else if (this.wrong > 0 && !all) this.setStatus(this.wrong >= HINT_AFTER ? '换个顺序再试试，也可以点「提示」' : '顺序还不太对，再读一读谜诗');
    else if (!all) this.setStatus('按谜诗的顺序，把符文放进星盘');
    else this.setStatus('都放好了，转动星盘吧');
  }

  private setStatus(t: string): void {
    if (this.status.textContent !== t) this.status.textContent = t;
  }

  private emit(): void {
    this.a.change([...this.slots]);
  }

  private tapSlot(i: number): void {
    if (this.solved || this.busy) return;
    if (i < this.hinted.length) return; // 提示揭开的格子锁定
    if (this.slots[i]) {
      this.slots[i] = null; // 取回托盘
      this.selected = i;
      this.emit();
    } else this.selected = this.selected === i ? -1 : i;
    this.render();
  }

  /** 点按托盘里的符文：放进选中的空格，没选就放进第一个空格；已经在盘上的点一下取回 */
  private tapRune(id: string): void {
    if (this.solved || this.busy) return;
    const at = this.slots.indexOf(id);
    if (at >= this.hinted.length && at >= 0) {
      this.slots[at] = null;
      this.emit();
      this.render();
      return;
    }
    if (this.hinted.includes(id)) return;
    let i = this.selected >= this.hinted.length && !this.slots[this.selected] ? this.selected : -1;
    if (i < 0) i = this.slots.findIndex((s, k) => k >= this.hinted.length && !s);
    if (i < 0) return;
    this.put(i, id);
  }

  private put(i: number, id: string): void {
    if (i < this.hinted.length || this.hinted.includes(id)) return;
    const prev = this.slots.indexOf(id);
    if (prev >= 0) this.slots[prev] = this.slots[i]; // 从另一格拖来：两格互换
    this.slots[i] = id;
    this.selected = -1;
    this.emit();
    this.render();
    const b = this.slotEls[i];
    b.classList.remove('is-pop');
    void b.offsetWidth;
    b.classList.add('is-pop');
  }

  // ── 拖动：托盘 → 格子（不到 8px 算点按） ──

  private dragStart(e: PointerEvent, id: string, from: HTMLElement): void {
    if (this.solved || this.busy || e.button !== 0) return;
    e.preventDefault();
    this.drag = { id, x: e.clientX, y: e.clientY, ghost: null, pointer: e.pointerId, from };
  }

  private readonly onMove = (e: PointerEvent): void => {
    const d = this.drag;
    if (!d || e.pointerId !== d.pointer) return;
    if (!d.ghost && Math.hypot(e.clientX - d.x, e.clientY - d.y) > 8) {
      const r = this.byId.get(d.id)!;
      d.ghost = el('div', 'dial-ghost');
      d.ghost.append(runeSvg(r.icon, 'dial-slot-icon'));
      document.body.append(d.ghost);
      d.from.classList.add('is-dragging');
    }
    if (d.ghost) {
      d.ghost.style.transform = `translate(${e.clientX - 30}px, ${e.clientY - 30}px)`;
      const over = this.slotAt(e.clientX, e.clientY);
      this.slotEls.forEach((b, i) => b.classList.toggle('is-over', i === over));
    }
  };

  private readonly onUp = (e: PointerEvent): void => {
    const d = this.drag;
    if (!d || e.pointerId !== d.pointer) return;
    this.drag = null;
    d.from.classList.remove('is-dragging');
    this.slotEls.forEach((b) => b.classList.remove('is-over'));
    if (!d.ghost) return this.tapRune(d.id);
    d.ghost.remove();
    const i = this.slotAt(e.clientX, e.clientY);
    if (i >= 0) this.put(i, d.id);
  };

  private readonly onCancel = (e: PointerEvent): void => {
    if (this.drag?.pointer !== e.pointerId) return;
    this.drag.ghost?.remove();
    this.drag.from.classList.remove('is-dragging');
    this.drag = null;
  };

  /** 屏幕点下的格子（按格子中心距离，手指不用很准：半径放大到 1.2 倍） */
  private slotAt(x: number, y: number): number {
    let best = -1;
    let bd = Infinity;
    this.slotEls.forEach((b, i) => {
      const r = b.getBoundingClientRect();
      const d = Math.hypot(x - (r.left + r.width / 2), y - (r.top + r.height / 2));
      if (d < r.width * 0.6 * 1.2 && d < bd) {
        bd = d;
        best = i;
      }
    });
    return best;
  }

  // ── 转动 / 提示 ──

  private check(): void {
    if (this.solved || this.busy) return;
    const ids = this.slotEls.map((_, i) => this.filled(i));
    if (ids.some((x) => x === null)) return;
    const ok = this.a.check(ids as string[]);
    if (!ok) {
      this.wrong += 1;
      this.busy = true;
      this.wheel.classList.remove('is-shake');
      void this.wheel.offsetWidth;
      this.wheel.classList.add('is-shake');
      window.setTimeout(() => {
        this.busy = false;
        this.wheel.classList.remove('is-shake');
        this.render();
      }, SHAKE_MS);
      this.render();
      this.setStatus('星盘轻轻晃了晃——顺序还不对');
      return;
    }
    this.solved = true;
    this.busy = true;
    this.selected = -1;
    this.render();
    this.setStatus('星盘转起来了……');
    this.wheel.classList.add('is-solved');
    window.setTimeout(() => {
      this.busy = false;
      this.wheel.classList.add('is-done');
      this.showPage(true);
    }, SPIN_MS);
  }

  private useHint(): void {
    if (this.solved || this.busy) return;
    const h = this.a.hint();
    if (!h) return;
    this.hinted = h;
    // 被提示占用的格子 / 符文从玩家摆的位置里拿掉
    for (let i = 0; i < this.slots.length; i++) {
      if (i < h.length || (this.slots[i] && h.includes(this.slots[i]!))) this.slots[i] = null;
    }
    this.emit();
    this.render();
    const k = h.length - 1;
    const r = this.byId.get(h[k]);
    if (r && !r.found) this.setStatus(`第${CN[k]}格是「${r.name}」——先回「${r.card}」找到它`);
    else this.setStatus(`第${CN[k]}格亮起来了`);
  }

  /** 《月亮之书》的一页（答对后翻开；已解开的星盘直接显示） */
  private showPage(fresh: boolean): void {
    const layer = el('div', `dial-book${fresh ? ' is-fresh' : ''}`);
    const card = el('section', 'dial-book-card');
    card.setAttribute('role', 'dialog');
    card.append(el('p', 'dial-book-name', `《${this.m.page.book}》`), el('h3', 'dial-book-title', this.m.page.title), el('p', 'dial-book-text', this.m.page.text));
    const ok = el('button', 'ui-btn ui-btn--yellow ui-btn--wide dial-book-ok');
    ok.type = 'button';
    ok.id = 'dial-book-ok';
    ok.append(el('span', '', this.m.last && fresh ? '翻到最后' : '合上书'));
    ok.onclick = () => this.a.close();
    card.append(ok);
    layer.append(card);
    this.root.append(layer);
  }
}
